import { useRef } from 'react';

/**
 * Five boxes for the 5-digit masjid code (the same as the iPhone app's Masjid Code screen). Typing moves along,
 * Backspace moves back, and pasting a whole code fills every box. `onComplete` runs when the fifth digit is in.
 */
export function CodeInput({ value, onChange, onComplete, disabled, bad, autoFocus }: {
  value: string; onChange: (v: string) => void; onComplete?: (v: string) => void; disabled?: boolean; bad?: boolean; autoFocus?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = value.padEnd(5, ' ').slice(0, 5).split('');
  const set = (next: string, focus: number) => {
    const clean = next.replace(/\D/g, '').slice(0, 5);
    onChange(clean);
    refs.current[Math.min(focus, 4)]?.focus();
    if (clean.length === 5) onComplete?.(clean);
  };
  return (
    <div className={`code-input${bad ? ' bad' : ''}`} role="group" aria-label="Masjid code, 5 digits">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className="code-box"
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={5}
          aria-label={`Digit ${i + 1} of 5`}
          value={d.trim()}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => {
            const typed = e.currentTarget.value.replace(/\D/g, '');
            if (typed.length > 1) { set(typed, typed.length); return; } // pasted, or iOS filled the code in
            const arr = value.split('');
            arr[i] = typed;
            const joined = arr.join('').slice(0, 5);
            set(joined, typed ? i + 1 : i);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !digits[i]!.trim() && i > 0) { e.preventDefault(); set(value.slice(0, i - 1) + value.slice(i), i - 1); }
            if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
            if (e.key === 'ArrowRight' && i < 4) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => { e.preventDefault(); set(e.clipboardData.getData('text'), 5); }}
        />
      ))}
    </div>
  );
}
